
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export const PWAInstallPrompt: React.FC = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    const beforeInstallPromptHandler = (e: Event) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      // Store the event so it can be triggered later
      setInstallPrompt(e as BeforeInstallPromptEvent);
      // Update UI to notify the user they can install the PWA
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', beforeInstallPromptHandler);

    // Check if the app is already installed
    window.addEventListener('appinstalled', () => {
      // Hide the app-provided install promotion
      setIsInstallable(false);
      // Clear the install prompt
      setInstallPrompt(null);
      // Log install to analytics
      console.log('PWA was installed');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstallPromptHandler);
    };
  }, []);

  const handleInstallClick = () => {
    if (!installPrompt) return;

    // Show the install prompt
    installPrompt.prompt();

    // Wait for the user to respond to the prompt
    installPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }
      // Clear the saved prompt since it can't be used again
      setInstallPrompt(null);
      setIsInstallable(false);
    });
  };

  // Only show install button if the app can be installed
  if (!isInstallable) return null;

  return (
    <div className="fixed bottom-4 right-4 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg z-50 max-w-sm border border-gray-200 dark:border-gray-700">
      <div className="flex flex-col space-y-2">
        <h3 className="font-medium text-gray-900 dark:text-white">Install JiMAT POS</h3>
        <p className="text-gray-700 dark:text-gray-300 text-sm">
          Install this application on your device for quick and easy access when you're on the go.
        </p>
        <div className="flex justify-end space-x-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setIsInstallable(false)}
          >
            Not now
          </Button>
          <Button 
            variant="default"
            size="sm"
            onClick={handleInstallClick}
          >
            Install
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
